import _ from 'lodash';
import UrlHelper from 'common/utils/url-helper';
import { ADMIN_SESSION, CLIENT_SESSION } from 'common/constants';
import { StoryMigrationAdapter } from 'utils/facades/story-migration-adapter';
import { isScreenshot } from 'src/routes/client/actions/api-events';

/**
 * @param url {String} request url
 * @return {string}
 */
function getKeyLocation(url) {
	const isAdminPath = _.startsWith(url, UrlHelper.adminBackend);
	return isAdminPath ? ADMIN_SESSION : CLIENT_SESSION;
}

/**
 * interceptor for api requests - adds session id and isBrowser keys
 *
 * @param {object} req - request object
 * @param {object} requestOptions - request options
 * @param {object} requestOptions.data - request data
 * @return req
 */
function apiRequest(req, requestOptions) {
	if (isScreenshot()) {
		req.cancel();
	}

	req.withCredentials();

	const keyLocation = getKeyLocation(req.url);

	_.set(requestOptions, ['data'], {
		data: requestOptions.data,
	});

	// Admin uses x-session-toke. Client uses cookies
	if (keyLocation === ADMIN_SESSION) {
		// get existing session id when in browser
		const sessionId = localStorage.getItem(keyLocation);

		if (sessionId) {
			_.set(requestOptions, ['headers', 'x-session-token'], sessionId);
		}
	}

	return req;
}

/**
 * apiClientResponse - interceptor for api responses - sets session id in local storage
 *
 * @param {object} req - request object
 * @param {object} res - response object
 * @param {object} options - request options
 * @return res
 */
function apiResponse(req, res, options) {
	const keyLocation = getKeyLocation(req.url);

	// Admin uses x-session-toke. Client uses cookies
	if (keyLocation === ADMIN_SESSION) {
		const sessionId = _.get(res, ['headers', 'x-session-token']);

		// store session id
		if (sessionId) {
			try {
				localStorage.setItem(keyLocation, sessionId);
			} catch (e) {
				console.error('unable to set id');
			}
		}
	}

	// Story migration adapter
	if (options.storyAdapter) {
		if (Array.isArray(res.body.result)) {
			res.body.result = res.body.result.map(story => {
				return new StoryMigrationAdapter({ story }).adapt();
			});
		} else if (res.body.result) {
			res.body.result = new StoryMigrationAdapter({ story: res.body.result }).adapt();
		}
	}

	return res;
}

/**
 * apiClientResponseBody - Response interceptor to get response data from response body
 *
 * @param  {object} req request
 * @param  {object} res response
 * @return {object}     response data
 * @param {object} options - request options
 */
function apiResponseBody(req, res, options) {
	return _.get(res, ['body', 'data'], res);
}

/**
 * interceptor for file upload requests
 *
 * @param {object} req - request object
 * @param {object} options - request options
 * @param {object} options.data - request data
 * @return req
 */
// function fileApiClientRequest(req, { data }) {
// 	req = req.attach(data.name, data.file, randomBytes(8).toString('hex'))
// 		.field({ 'fileType': _.get(data, ['fileType'], '') })
// 		.withCredentials();
//
// 	return req;
// }

/**
 * Attaches a bearer json web token to the Authorization request header
 * @param {object} req
 * @return {object} request object
 */
// function attachJwtToken(req) {
// 	req.set('Authorization', `Bearer ${localStorage.getItem(USER_TOKEN_ID)}`);
//
// 	return req;
// }

/**
 *
 */
function getSessionId(organizationId): string | undefined {
	const keyLocation = getKeyLocation(window.location.href);

	if (organizationId) {
		const organizations = JSON.parse(localStorage.getItem('organizations') || '{}');
		return organizations[organizationId] ?? undefined;
	}

	return localStorage.getItem(keyLocation) ?? undefined;
}

export {
	// attachJwtToken,
	// fileApiClientRequest,
	apiRequest,
	apiResponse,
	apiResponseBody,
	getSessionId,
};
